import React, { useState } from 'react';
import { Table, TableColumnsType, Button, Space, Modal, message, Tag } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import FilterArea, { FilterOptions, DEFAULT_FILTER_OPTIONS } from './components/FilterArea';
import RuleFormDrawer from './components/RuleFormDrawer';
import { HasResource } from '../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';
import {
  GET_ADDRESS_WHITELIST_RULE_LIST,
  UPDATE_ADDRESS_WHITELIST_RULE_STATUS,
  DELETE_ADDRESS_WHITELIST_RULE,
  GetAddressWhitelistRuleListResponse,
  AddressWhitelistRuleType,
  RULE_TYPE_BASIC,
  STATUS_ENABLED,
  STATUS_DISABLED,
} from './graphql/addressWhitelistRuleGraphql';

const AddressWhitelistRulePage: React.FC = () => {
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [appliedFilters, setAppliedFilters] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerKey, setDrawerKey] = useState(0);
  const [editingRecord, setEditingRecord] = useState<AddressWhitelistRuleType | null>(null);
  const { t } = useTranslation();

  const { data, loading, refetch } = useQuery<GetAddressWhitelistRuleListResponse>(
    GET_ADDRESS_WHITELIST_RULE_LIST,
    {
      variables: {
        ruleName: appliedFilters.ruleName || undefined,
        ruleType: appliedFilters.ruleType ? Number(appliedFilters.ruleType) : undefined,
        status: appliedFilters.status ? Number(appliedFilters.status) : undefined,
        state: appliedFilters.state || undefined,
        city: appliedFilters.city || undefined,
        page: currentPage,
        size: pageSize,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const [updateStatus] = useMutation(UPDATE_ADDRESS_WHITELIST_RULE_STATUS, {
    onCompleted: () => refetch(),
    onError: (err) => message.error(err.message),
  });

  const [deleteRule] = useMutation(DELETE_ADDRESS_WHITELIST_RULE, {
    onCompleted: () => refetch(),
    onError: (err) => message.error(err.message),
  });

  const handleFilter = () => {
    setAppliedFilters({ ...filterOptions });
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilterOptions(DEFAULT_FILTER_OPTIONS);
    setAppliedFilters(DEFAULT_FILTER_OPTIONS);
    setCurrentPage(1);
  };

  const handleTableChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const openCreate = () => {
    setEditingRecord(null);
    setDrawerKey((k) => k + 1);
    setDrawerVisible(true);
  };

  const openEdit = (record: AddressWhitelistRuleType) => {
    setEditingRecord(record);
    setDrawerKey((k) => k + 1);
    setDrawerVisible(true);
  };

  const showDeleteConfirm = (record: AddressWhitelistRuleType) => {
    Modal.confirm({
      title: t('mapTool.addressWhitelistRule.deleteTitle') as string,
      content: t('mapTool.addressWhitelistRule.deleteContent') as string,
      okText: t('components.button.confirm') as string,
      okType: 'danger',
      cancelText: t('components.button.cancel') as string,
      onOk: () => deleteRule({ variables: { id: Number(record.id) } }),
    });
  };

  const toggleStatus = (record: AddressWhitelistRuleType) => {
    const nextStatus = record.status === STATUS_ENABLED ? STATUS_DISABLED : STATUS_ENABLED;
    updateStatus({ variables: { id: Number(record.id), status: nextStatus } });
  };

  const columns: TableColumnsType<AddressWhitelistRuleType> = [
    {
      title: t('mapTool.addressWhitelistRule.table.id'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center',
    },
    {
      title: t('mapTool.addressWhitelistRule.table.ruleName'),
      dataIndex: 'ruleName',
      key: 'ruleName',
      width: 160,
      ellipsis: true,
    },
    {
      title: t('mapTool.addressWhitelistRule.table.priority'),
      dataIndex: 'priority',
      key: 'priority',
      width: 90,
      align: 'center',
    },
    {
      title: t('mapTool.addressWhitelistRule.table.ruleType'),
      dataIndex: 'ruleType',
      key: 'ruleType',
      width: 110,
      align: 'center',
      render: (v: number) =>
        v === RULE_TYPE_BASIC
          ? t('mapTool.addressWhitelistRule.ruleType.basic')
          : t('mapTool.addressWhitelistRule.ruleType.regex'),
    },
    {
      title: t('mapTool.addressWhitelistRule.table.status'),
      dataIndex: 'status',
      key: 'status',
      width: 90,
      align: 'center',
      render: (v: number) =>
        v === STATUS_ENABLED ? (
          <Tag color="green">{t('mapTool.addressWhitelistRule.status.enabled')}</Tag>
        ) : (
          <Tag color="default">{t('mapTool.addressWhitelistRule.status.disabled')}</Tag>
        ),
    },
    {
      title: t('mapTool.addressWhitelistRule.table.state'),
      dataIndex: 'state',
      key: 'state',
      width: 100,
      align: 'center',
      render: (v: string | null | undefined) => v ?? '—',
    },
    {
      title: t('mapTool.addressWhitelistRule.table.city'),
      dataIndex: 'city',
      key: 'city',
      width: 140,
      ellipsis: true,
      render: (v: string | null | undefined) => v ?? '—',
    },
    {
      title: t('mapTool.addressWhitelistRule.table.addressId'),
      dataIndex: 'addressId',
      key: 'addressId',
      width: 110,
      align: 'center',
    },
    {
      title: t('mapTool.addressWhitelistRule.table.fullStandardAddress'),
      dataIndex: 'fullStandardAddress',
      key: 'fullStandardAddress',
      width: 320,
      ellipsis: true,
    },
    {
      title: t('mapTool.addressWhitelistRule.table.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      align: 'center',
      render: (v: string | null | undefined) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '—'),
    },
    {
      title: t('components.button.action') as string,
      key: 'action',
      width: 220,
      fixed: 'right' as const,
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          <HasResource resourceCodes={[ResourceCode.addressWhitelistRule.UPDATE]}>
            <Button type="link" size="small" onClick={() => openEdit(record)}>
              {t('components.button.edit')}
            </Button>
            <Button type="link" size="small" onClick={() => toggleStatus(record)}>
              {record.status === STATUS_ENABLED
                ? t('mapTool.addressWhitelistRule.actions.disable')
                : t('mapTool.addressWhitelistRule.actions.enable')}
            </Button>
          </HasResource>
          <HasResource resourceCodes={[ResourceCode.addressWhitelistRule.DELETE]}>
            <Button type="link" size="small" danger onClick={() => showDeleteConfirm(record)}>
              {t('components.button.delete')}
            </Button>
          </HasResource>
        </Space>
      ),
    },
  ];

  const pageData = data?.addressWhitelistRuleList;

  return (
    <div className="p-6">
      <h4 className="text-xl font-bold mb-4">{t('mapTool.addressWhitelistRule.pageTitle')}</h4>

      <FilterArea filters={filterOptions} setFilters={setFilterOptions} />

      <div className="flex justify-end items-center mb-4 mt-4">
        <div className="flex items-center gap-2">
          <HasResource resourceCodes={[ResourceCode.addressWhitelistRule.CREATE]}>
            <Button type="primary" onClick={openCreate} className="w-auto min-w-24">
              <span className="font-bold">{t('components.button.new')}</span>
            </Button>
          </HasResource>
          <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
            <span className="font-bold">{t('components.button.filter')}</span>
          </Button>
          <Button onClick={handleResetFilters} className="w-auto min-w-24">
            <span className="font-bold">{t('components.button.reset')}</span>
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <Table
          columns={columns}
          dataSource={pageData?.content || []}
          loading={loading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: pageData?.totalElements || 0,
            showSizeChanger: true,
            showTotal: (total) => t('mapTool.addressWhitelistRule.table.paginationTotal', { total }),
            onChange: handleTableChange,
            onShowSizeChange: handleTableChange,
          }}
        />
      </div>

      <RuleFormDrawer
        key={drawerKey}
        visible={drawerVisible}
        record={editingRecord}
        onClose={() => setDrawerVisible(false)}
        onSuccess={() => refetch()}
      />
    </div>
  );
};

export default AddressWhitelistRulePage;
